import { useCallback, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Popover } from '@opendoor/atoms/popover';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import { PriceFilter } from 'components/marketplace/search/PriceFilter';
import type { ErrorKey, FilterErrors } from 'components/marketplace/search/types';
import {
  MARKETPLACE_POPOVER_SURFACE_CLASS,
  type PopoverTriggerRender,
} from 'components/marketplace/shared/popoverStyles';

const EMPTY_FILTER_ERRORS: FilterErrors = {};

const compactPriceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 2,
});

const formatPrice = (dollars: number): string => compactPriceFormatter.format(dollars);

export const quickPriceLabel = (
  minPrice: number | null | undefined,
  maxPrice: number | null | undefined,
): string => {
  if (minPrice != null && maxPrice != null) {
    return `${formatPrice(minPrice)}–${formatPrice(maxPrice)}`;
  }
  if (minPrice != null) {
    return `${formatPrice(minPrice)}+`;
  }
  if (maxPrice != null) {
    return `Up to ${formatPrice(maxPrice)}`;
  }
  return 'Price';
};

interface QuickPriceFilterProps {
  minPrice?: number | null;
  maxPrice?: number | null;
  onChange: (minPrice: number | null, maxPrice: number | null) => void;
}

export const QuickPriceFilter = ({ minPrice, maxPrice, onChange }: QuickPriceFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftMinPrice, setDraftMinPrice] = useState<number | null>(minPrice ?? null);
  const [draftMaxPrice, setDraftMaxPrice] = useState<number | null>(maxPrice ?? null);
  const [errors, setErrors] = useState(EMPTY_FILTER_ERRORS);

  const handleError = useCallback((key: ErrorKey, value: string) => {
    setErrors((current) => ({ ...current, [key]: value }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraftMinPrice(minPrice ?? null);
    setDraftMaxPrice(maxPrice ?? null);
    setErrors(EMPTY_FILTER_ERRORS);
  }, [maxPrice, minPrice]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        resetDraft();
      }
      setIsOpen(open);
    },
    [resetDraft],
  );

  const handleApply = useCallback(() => {
    if (errors.minPrice || errors.maxPrice) {
      return;
    }
    onChange(draftMinPrice, draftMaxPrice);
    setIsOpen(false);
  }, [draftMaxPrice, draftMinPrice, errors.maxPrice, errors.minPrice, onChange]);

  const label = quickPriceLabel(minPrice, maxPrice);
  const renderTrigger = useCallback<PopoverTriggerRender>(
    (props) => (
      <Button
        {...props}
        analyticsName="marketplace-price-quick-filter"
        aria-label={`Price filter: ${label}`}
        size="sm"
        variant="secondary"
        className="h-10 shrink-0 px-3"
      >
        {label}
        <IconChevronDown size={16} aria-hidden />
      </Button>
    ),
    [label],
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger render={renderTrigger} />
      <Popover align="start" className={cn('w-90 p-4', MARKETPLACE_POPOVER_SURFACE_CLASS)}>
        <Col className="gap-4">
          <PriceFilter
            minPrice={draftMinPrice}
            maxPrice={draftMaxPrice}
            handleMinPriceChange={setDraftMinPrice}
            handleMaxPriceChange={setDraftMaxPrice}
            handleError={handleError}
            errors={errors}
          />
          <Button
            analyticsName="marketplace-price-quick-filter-apply"
            aria-label="Apply price range"
            size="sm"
            variant="primary"
            className="w-full"
            disabled={Boolean(errors.minPrice || errors.maxPrice)}
            onClick={handleApply}
          >
            Apply
          </Button>
        </Col>
      </Popover>
    </Popover.Root>
  );
};
